import { isDefined, indexBy } from 'remeda'
import { FairlyPrioritizedUsers } from '@chilipiper/api-client/src/distribution'
import {
  AssignmentRequestAssignmentWithKnownAssigneeRequest,
  OwnershipAssigneeEvaluationV3,
  OwnershipSettingsV4,
  PublicOwnershipSchedulingLinkV2Assignment,
} from '@chilipiper/api-client/src/team-scheduling'
import {
  DistributionAttendeesList,
  OwnershipSchedulingLinkAssignments,
  OwnershipSchedulingLinkAssignments1,
  RoundRobinAvailableAttendees,
  RoundRobinExpectedHostV2,
  RoundRobinSchedulingLinkAssignment,
  RoundRobinSchedulingLinkAssignment1,
  StrictRoundRobin1,
} from '@chilipiper/api-client/src/user-availability'
import { UsersAvailability, MultiRoundRobinAvailability } from '../../load-availability-options'
import {
  OwnershipLinkRoutingResult,
  isOwnerFromSimpleRule,
  isMixedResults,
  isOwner,
  isDistributionUsers,
  isWithDistribution,
  MixedResult,
  WithDistribution,
} from '../types'
import {
  AssignmentRequest,
  AssignmentRequestAssignedViaDistribution,
  AssignmentRequestAssignedViaTeam,
  AssignmentRequestWithUnresolvedDistribution,
  DistributionAssignmentsAttendance,
  DistributionAssignmentsAttendanceMap,
} from '../../../types'

export const mapOwnershipLinkRoutingResultToAssignments = (
  strictAssignees?: StrictRoundRobin1[],
  routingResult?: OwnershipLinkRoutingResult
): AssignmentRequestAssignmentWithKnownAssigneeRequest[] => {
  if (!routingResult) {
    return []
  }

  const mappedAvailableAssignees = indexBy(strictAssignees || [], item => item.distributionId)

  if (isMixedResults(routingResult)) {
    return routingResult.result.flatMap(item => {
      if (isOwner(item)) {
        return {
          assigneeId: item.ownerId,
          teamId: item.teamId,
          rule: {
            ruleId: item.ruleId,
            type: 'SharedRule' as const,
          },
          type: 'AssignedViaTeam' as const,
        }
      }

      if (isDistributionUsers(item)) {
        const availableAssignee = mappedAvailableAssignees[item.id]
        if (!availableAssignee) {
          return []
        }
        return {
          assigneeId: availableAssignee.userId,
          distributionId: item.id,
          type: 'AssignedViaDistribution' as const,
        }
      }

      return []
    })
  }

  if (isOwnerFromSimpleRule(routingResult)) {
    return [
      {
        assigneeId: routingResult.ownerId,
        teamId: routingResult.teamId,
        rule: {
          type: 'SimpleRule' as const,
        },
        type: 'AssignedViaTeam' as const,
      },
    ]
  }

  if (isWithDistribution(routingResult)) {
    return routingResult.distributions.flatMap(distribution => {
      const availableAssignee = mappedAvailableAssignees[distribution.id]
      if (!availableAssignee) {
        return []
      }
      return {
        assigneeId: availableAssignee.userId,
        distributionId: distribution.id,
        type: 'AssignedViaDistribution' as const,
      }
    })
  }

  return []
}

export const mapOwnershipLinkRoutingResultToAssignmentsUnresolvedAssignees = (
  rrAssignees?: RoundRobinAvailableAttendees[],
  routingResult?: OwnershipLinkRoutingResult
): AssignmentRequest[] => {
  if (!routingResult) {
    return []
  }
  //TOOD: handle optional assignee
  const mappedAvailableAssignees = indexBy(rrAssignees || [], item => item.distributionId)

  if (isMixedResults(routingResult)) {
    return routingResult.result.flatMap(item => {
      if (isOwner(item)) {
        return {
          assigneeId: item.ownerId,
          teamId: item.teamId,
          rule: {
            ruleId: item.ruleId,
            type: 'SharedRule' as const,
          },
          type: 'AssignedViaTeam' as const,
        } satisfies AssignmentRequestAssignedViaTeam
      }

      if (isDistributionUsers(item)) {
        const availableAssignee = mappedAvailableAssignees[item.id]
        if (!availableAssignee) {
          return []
        }

        if (availableAssignee.type === 'StrictRoundRobin') {
          return {
            assigneeId: availableAssignee.userId,
            distributionId: item.id,
            type: 'AssignedViaDistribution' as const,
          } satisfies AssignmentRequestAssignedViaDistribution
        } else {
          return {
            availableUsers: availableAssignee.userIds,
            distributionId: item.id,
            attendance: 'Required', // TODO: update this based on settings
            type: 'WithUnresolvedDistribution' as const,
          } satisfies AssignmentRequestWithUnresolvedDistribution
        }
      }

      return []
    })
  }

  if (isOwnerFromSimpleRule(routingResult)) {
    return [
      {
        assigneeId: routingResult.ownerId,
        teamId: routingResult.teamId,
        rule: {
          type: 'SimpleRule' as const,
        },
        type: 'AssignedViaTeam' as const,
      },
    ]
  }

  if (isWithDistribution(routingResult)) {
    return routingResult.distributions.flatMap(distribution => {
      const availableAssignee = mappedAvailableAssignees[distribution.id]
      if (!availableAssignee) {
        return []
      }

      if (availableAssignee.type === 'StrictRoundRobin') {
        return {
          assigneeId: availableAssignee.userId,
          distributionId: distribution.id,
          type: 'AssignedViaDistribution' as const,
        } satisfies AssignmentRequestAssignedViaDistribution
      } else {
        return {
          availableUsers: availableAssignee.userIds,
          distributionId: distribution.id,
          attendance: 'Required', // TODO: update this based on settings
          type: 'WithUnresolvedDistribution' as const,
        } satisfies AssignmentRequestWithUnresolvedDistribution
      }
    })
  }

  return []
}

export const getFirstAssignmentFromRoutingResult = (
  routingResult: OwnershipLinkRoutingResult
): { userId: string } | { distributionId: string } => {
  // Handle OwnerFromSimpleRule - return the owner
  if (isOwnerFromSimpleRule(routingResult)) {
    return { userId: routingResult.ownerId }
  }

  // Handle MixedResults - return first assignment (owner or distribution)
  if (isMixedResults(routingResult) && routingResult.result.length > 0) {
    const firstItem = routingResult.result[0]

    if (isOwner(firstItem)) {
      return { userId: firstItem.ownerId }
    }

    if (isDistributionUsers(firstItem)) {
      return { distributionId: firstItem.id }
    }
  }

  // Handle WithDistribution - return first distribution
  if (isWithDistribution(routingResult) && routingResult.distributions.length > 0) {
    return { distributionId: routingResult.distributions[0].id }
  }

  throw new Error('No first assignment found')
}

type DistributionUsers = FairlyPrioritizedUsers & { id: string }

const getOwnerIdsFromRoutingResult = (routingResult?: OwnershipLinkRoutingResult): string[] => {
  if (!routingResult) return []

  if (isOwnerFromSimpleRule(routingResult)) {
    return [routingResult.ownerId]
  }

  if (isMixedResults(routingResult)) {
    const ownerIds: string[] = []
    routingResult.result.forEach(item => {
      if (isOwner(item)) {
        ownerIds.push(item.ownerId)
      }
    })
    return ownerIds
  }

  return []
}

export const mapDistributionAssignmentsAttendanceById = (
  distributionAssignments?: DistributionAssignmentsAttendance
): DistributionAssignmentsAttendanceMap => {
  const assignments = new Map<string, 'Required' | 'Optional'>()

  if (!distributionAssignments) {
    return assignments
  }

  distributionAssignments.forEach(({ distributionId, attendance }) => {
    assignments.set(distributionId, attendance)
  })

  return assignments
}

/** Get distributions from OwnershipLinkRoutingResult */
const getDistributionsFromRoutingResult = (
  routingResult?: OwnershipLinkRoutingResult
): DistributionUsers[] => {
  if (!routingResult) return []

  if (isWithDistribution(routingResult)) {
    return routingResult.distributions
  }

  if (isMixedResults(routingResult)) {
    const distributions: DistributionUsers[] = []
    routingResult.result.forEach(item => {
      if (isDistributionUsers(item)) {
        distributions.push(item)
      }
    })
    return distributions
  }

  return []
}

export const getRoutingResultAvailabilityOptions = ({
  routingResult,
  expectedHost,
  additionalRequiredAttendees,
}: {
  routingResult?: OwnershipLinkRoutingResult
  expectedHost?: RoundRobinExpectedHostV2
  additionalRequiredAttendees?: string[]
}): UsersAvailability | MultiRoundRobinAvailability | undefined => {
  if (!routingResult || !expectedHost) {
    return undefined
  }

  const owners = getOwnerIdsFromRoutingResult(routingResult)
  const distributions = getDistributionsFromRoutingResult(routingResult)

  if (isOwnerFromSimpleRule(routingResult)) {
    return {
      userIds: [routingResult.ownerId],
      expectedHost:
        expectedHost.type === 'User'
          ? expectedHost
          : { type: 'User', userId: routingResult.ownerId },
      additionalRequiredAttendees,
      meetingLimit: {
        type: 'NoMeetingLimit' as const,
      },
    } satisfies UsersAvailability
  }

  if (isMixedResults(routingResult)) {
    if (distributions.length === 0 && owners.length > 0) {
      return {
        userIds: owners,
        expectedHost:
          expectedHost.type === 'User' ? expectedHost : { type: 'User', userId: owners[0] },
        additionalRequiredAttendees,
        meetingLimit: {
          type: 'NoMeetingLimit' as const,
        },
      } satisfies UsersAvailability
    }

    // If has distributions (with or without owners)
    if (distributions.length > 0) {
      // Get attendees from distributions based on their users
      const distributionAttendees = distributions.map(dist => ({
        userIds: dist.users || [],
        distributionId: dist.id,
        required: routingResult.attendance.get(dist.id) === 'Required',
      })) satisfies DistributionAttendeesList[]

      const additionalAttendees = [...(additionalRequiredAttendees ?? []), ...owners]

      return {
        distributionAttendees,
        expectedHost,
        additionalRequiredAttendees: additionalAttendees.length ? additionalAttendees : undefined,
        ownershipAndUsersAttendees: owners,
      } satisfies MultiRoundRobinAvailability
    }
  }

  // Handle WithDistribution - only distributions
  if (isWithDistribution(routingResult) && routingResult.distributions.length > 0) {
    const distributionAttendees = routingResult.distributions.map(dist => ({
      userIds: dist.users || [],
      distributionId: dist.id,
      required: routingResult.attendance.get(dist.id) === 'Required',
    })) satisfies DistributionAttendeesList[]

    return {
      distributionAttendees,
      expectedHost,
      additionalRequiredAttendees,
      ownershipAndUsersAttendees: [],
    } satisfies MultiRoundRobinAvailability
  }

  return undefined
}

export const transformOwnershipAssignmentsToRoutingResult = (
  assignments?: OwnershipSchedulingLinkAssignments1,
  distributions?: DistributionUsers[],
  distributionAssignments?: PublicOwnershipSchedulingLinkV2Assignment[],
  ownershipSettings?: OwnershipSettingsV4,
  assigneeEvaluationV3?: OwnershipAssigneeEvaluationV3
): OwnershipLinkRoutingResult | undefined => {
  if (!assignments) return undefined

  const mappedDistributionAttendance =
    mapDistributionAssignmentsAttendanceById(distributionAssignments)

  if (assignments.type === 'WithLegacySingleDistribution') {
    if (!distributionAssignments?.length) {
      return undefined
    }

    // Map distributions in the order defined by ownershipLink.assignments
    const orderedDistributions = distributionAssignments
      .map(assignment => distributions?.find(d => d.id === assignment.distributionId))
      .filter(isDefined)

    if (orderedDistributions.length === 0) {
      return undefined
    }

    const assigneeId = assignments.assigneeId
    const firstDistribution = orderedDistributions[0]

    // If assignee is specified and is in the first distribution, update it
    if (assigneeId && firstDistribution.users?.includes(assigneeId)) {
      const [first, ...rest] = orderedDistributions
      return {
        distributions: [{ ...first, users: [assigneeId] }, ...rest],
        attendance: mappedDistributionAttendance,
      }
    }

    // Otherwise return ordered distributions as is
    return {
      distributions: orderedDistributions,
      attendance: mappedDistributionAttendance,
    }
  }

  if (assignments.type === 'WithSimpleRule') {
    const simpleRuleAttendance =
      ownershipSettings?.type === 'WithSimpleRule' ? ownershipSettings.attendance : 'Required'
    return {
      ownerId: assignments.assigneeId,
      teamId: assignments.teamId,
      attendance: simpleRuleAttendance,
    }
  }

  if (assignments.type === 'WithLegacySimpleRule') {
    if (
      assigneeEvaluationV3?.type === 'MixedRoutingResults' &&
      assigneeEvaluationV3.results?.length > 0
    ) {
      const strategies =
        ownershipSettings?.type === 'WithMixedRouting' ? ownershipSettings.strategies : []

      const firstResult = assigneeEvaluationV3.results[0]
      if (firstResult.type !== 'Distributed' && firstResult.teamRef?.teamId) {
        const strategy = strategies.find(
          str => str.type === 'RuleBasedRouting' && str.rule.id === firstResult.ruleId
        )
        return {
          result: [
            {
              ownerId: assignments.assigneeId,
              teamId: firstResult.teamRef.teamId,
              ruleId: firstResult.ruleId,
            },
          ],
          attendance: new Map<string, 'Required' | 'Optional'>().set(
            firstResult.ruleId,
            strategy?.attendance ?? 'Required'
          ),
        }
      }
    } else if (assigneeEvaluationV3?.type === 'SimpleRuleResult') {
      const attendance =
        ownershipSettings?.type === 'WithSimpleRule' ? ownershipSettings.attendance : 'Required'
      return {
        ownerId: assignments.assigneeId,
        teamId: assigneeEvaluationV3.teamRef.teamId,
        attendance,
      }
    }

    // If we can't determine the team, return undefined
    return undefined
  }

  if (assignments.type === 'WithMixedRouting' && assignments.assignments) {
    const results: MixedResult[] = []
    const attendance = new Map<string, 'Required' | 'Optional'>()

    const mixedStrategies =
      ownershipSettings?.type === 'WithMixedRouting' ? ownershipSettings.strategies : []

    assignments.assignments.forEach(assignment => {
      if (assignment.type === 'FromDistributionAssignment') {
        const distribution = distributions?.find(d => d.id === assignment.distributionId)
        if (distribution) {
          // If assignment has an assigneeId, check if it's in the distribution
          if (assignment.assigneeId && distribution.users?.includes(assignment.assigneeId)) {
            // Create a distribution with only the assigned user
            results.push({
              ...distribution,
              users: [assignment.assigneeId],
            })

            const strategy = mixedStrategies.find(
              str =>
                str.type === 'DistributionBasedRouting' &&
                str.distributionId === assignment.distributionId
            )
            attendance.set(assignment.distributionId, strategy?.attendance ?? 'Required')
          } else if (!assignment.assigneeId) {
            // If no specific assignee, include the whole distribution
            results.push(distribution)
            const strategy = mixedStrategies.find(
              str =>
                str.type === 'DistributionBasedRouting' &&
                str.distributionId === assignment.distributionId
            )
            attendance.set(assignment.distributionId, strategy?.attendance ?? 'Required')
          }
        }
      } else if (assignment.type === 'FromOwnershipRuleAssignment') {
        const strategy = mixedStrategies.find(
          str => str.type === 'RuleBasedRouting' && str.rule.id === assignment.rule
        )
        attendance.set(assignment.rule, strategy?.attendance ?? 'Required')
        results.push({
          ownerId: assignment.assigneeId,
          teamId: assignment.teamId,
          ruleId: assignment.rule,
        })
      }
    })

    if (results.length > 0) {
      return {
        result: results,
        attendance,
      }
    }
  }

  return undefined
}

export const transformAssigneeEvaluationToRoutingResult = (
  assigneeEvaluation?: OwnershipAssigneeEvaluationV3,
  distributions?: DistributionUsers[],
  distributionAssignments?: PublicOwnershipSchedulingLinkV2Assignment[],
  ownershipSettings?: OwnershipSettingsV4
): OwnershipLinkRoutingResult | undefined => {
  if (!assigneeEvaluation) {
    // If no assigneeEvaluation but we have distributions, return WithDistribution
    if (distributions && distributions.length > 0) {
      return {
        distributions: distributions,
        attendance: mapDistributionAssignmentsAttendanceById(distributionAssignments),
      }
    }
    return undefined
  }

  switch (assigneeEvaluation.type) {
    case 'Distributed': {
      const distributionsMapped = assigneeEvaluation.distributionIds
        ?.map(distributionId => {
          const distribution = distributions?.find(d => d.id === distributionId)
          return distribution
        })
        .filter(isDefined)

      const distributionIds = new Set(distributionsMapped?.map(d => d.id))
      const filteredDistributionAssignments = distributionAssignments?.filter(
        assignment => assignment.distributionId && distributionIds.has(assignment.distributionId)
      )

      return {
        distributions: distributionsMapped || [],
        attendance: mapDistributionAssignmentsAttendanceById(filteredDistributionAssignments),
      }
    }

    case 'MixedRoutingResults': {
      const mixedOwnershipSettings =
        ownershipSettings?.type === 'WithMixedRouting' ? ownershipSettings.strategies : []
      const attendance = new Map<string, 'Required' | 'Optional'>()

      const mixedResults: MixedResult[] = assigneeEvaluation.results
        ?.map(result => {
          if (result.type === 'Distributed') {
            const distribution = distributions?.find(d => d.id === result.distributionId)
            if (distribution) {
              attendance.set(
                distribution?.id,
                mixedOwnershipSettings.find(
                  str =>
                    str.type === 'DistributionBasedRouting' &&
                    str.distributionId === result.distributionId
                )?.attendance || 'Required'
              )
            }
            return distribution
          } else
            attendance.set(
              result.ruleId,
              mixedOwnershipSettings.find(
                str => str.type === 'RuleBasedRouting' && str.rule.id === result.ruleId
              )?.attendance || 'Required'
            )
          return {
            ownerId: result.ownerId,
            teamId: result.teamRef.teamId,
            ruleId: result.ruleId,
          }
        })
        .filter(isDefined)

      return {
        result: mixedResults,
        attendance,
      }
    }

    case 'SimpleRuleResult': {
      return {
        ownerId: assigneeEvaluation.ownerId,
        teamId: assigneeEvaluation.teamRef.teamId,
        attendance:
          ownershipSettings?.type === 'WithSimpleRule' ? ownershipSettings.attendance : 'Required',
      }
    }

    default:
      return undefined
  }
}

export const getDistributionIdsFromRoundRobinAssignments = (
  assignments?: RoundRobinSchedulingLinkAssignment[],
  linkDistributionIds?: string[]
): string[] => {
  // If no assignments, fallback to link's distributionIds
  if (!assignments || assignments.length === 0) {
    return linkDistributionIds || []
  }

  // Check if there's any LegacyAssignment
  const hasLegacyAssignment = assignments.some(a => a.type === 'LegacyAssignment')

  // If there's any LegacyAssignment, return the full link's distributionIds
  if (hasLegacyAssignment) {
    return linkDistributionIds || []
  }

  // Otherwise, collect distributionIds from Assignment types
  const distributionIds: string[] = []

  assignments.forEach(assignment => {
    if (assignment.type === 'Assignment' && assignment.distributionId) {
      if (!distributionIds.includes(assignment.distributionId)) {
        distributionIds.push(assignment.distributionId)
      }
    }
  })

  // If no distribution IDs found in assignments, fallback to link's distributionIds
  return distributionIds.length > 0 ? distributionIds : linkDistributionIds || []
}

export const transformRoundRobinAssignmentsToRoutingResult = (
  assignments?: RoundRobinSchedulingLinkAssignment1[],
  distributions?: DistributionUsers[], // should be ordered like in the link
  distributionAssignment?: DistributionAssignmentsAttendance
): WithDistribution | undefined => {
  if (!distributions || distributions.length === 0) {
    return undefined
  }

  // If no assignments provided, return all distributions
  if (!assignments || assignments.length === 0) {
    return {
      distributions: distributions,
      attendance: mapDistributionAssignmentsAttendanceById(distributionAssignment),
    }
  }

  // Check if there's any LegacyAssignment
  const hasLegacyAssignment = assignments.some(a => a.type === 'LegacyAssignment')

  // If there's any LegacyAssignment, return all distributions
  if (hasLegacyAssignment) {
    // Check if there's a specific assigneeId in the legacy assignment
    // there never be multiple LegacyAssignment in the array
    const legacyAssignment = assignments.find(a => a.type === 'LegacyAssignment')
    if (legacyAssignment?.assigneeId && distributions.length > 0) {
      const [first, ...rest] = distributions
      if (first.users?.includes(legacyAssignment.assigneeId)) {
        return {
          distributions: [{ ...first, users: [legacyAssignment.assigneeId] }, ...rest],
          attendance: mapDistributionAssignmentsAttendanceById(distributionAssignment),
        }
      }
    }

    return {
      distributions: distributions,
      attendance: mapDistributionAssignmentsAttendanceById(distributionAssignment),
    }
  }

  // Handle new Assignment types
  const distributionsList: DistributionUsers[] = []
  const attendanceMap = new Map<string, 'Required' | 'Optional'>()

  assignments.forEach(assignment => {
    if (assignment.type === 'Assignment' && assignment.distributionId) {
      const distribution = distributions.find(d => d.id === assignment.distributionId)
      if (distribution) {
        // If assignment has specific assigneeId, narrow the distribution to that user
        attendanceMap.set(assignment.distributionId, assignment.attendance)
        if (
          assignment.attendance === 'Required' &&
          assignment.assigneeId &&
          distribution.users?.includes(assignment.assigneeId)
        ) {
          distributionsList.push({
            ...distribution,
            users: [assignment.assigneeId],
          })
        } else {
          // No specific assignee, use the full distribution
          distributionsList.push(distribution)
        }
      }
    }
  })

  // Return the distributions we found
  return distributionsList.length > 0
    ? {
        distributions: distributionsList,
        attendance: attendanceMap,
      }
    : undefined
}

export const getDistributionIdsFromAssigneeEvaluation = (
  assigneeEvaluation?: OwnershipAssigneeEvaluationV3
): string[] => {
  if (!assigneeEvaluation) return []

  switch (assigneeEvaluation.type) {
    case 'Distributed': {
      return assigneeEvaluation.distributionIds || []
    }

    case 'MixedRoutingResults': {
      const distributionIds: string[] = []

      assigneeEvaluation.results?.forEach(result => {
        if (result.type === 'Distributed' && result.distributionId) {
          distributionIds.push(result.distributionId)
        }
      })

      return distributionIds
    }

    case 'SimpleRuleResult': {
      return []
    }

    default:
      return []
  }
}

/**
 * Applies selected assignees to OwnershipLinkRoutingResult. Supports both WithDistribution and
 * MixedResults types.
 *
 * @param routingResult - The routing result (WithDistribution, MixedResults, or
 *   OwnerFromSimpleRule)
 * @param selectedAssigneeIds - Array of assignee IDs indexed by distribution position
 * @returns Updated routing result with assignees applied, or undefined
 */
export const applySelectedAssigneesToRoutingResult = (
  routingResult?: OwnershipLinkRoutingResult,
  selectedAssigneeIds?: string[]
): OwnershipLinkRoutingResult | undefined => {
  if (!routingResult || !selectedAssigneeIds || selectedAssigneeIds.length === 0) {
    return routingResult
  }

  // Handle WithDistribution type
  if (isWithDistribution(routingResult)) {
    return applySelectedAssigneesToWithDistribution(routingResult, selectedAssigneeIds)
  }

  // Handle MixedResults type
  if (isMixedResults(routingResult) && routingResult.result.length > 0) {
    let distributionIndex = 0
    const updatedResult = routingResult.result.map(item => {
      // Only process distribution items, skip owners
      if (isDistributionUsers(item)) {
        const assigneeId = selectedAssigneeIds[distributionIndex]
        distributionIndex++

        if (assigneeId && item.users?.includes(assigneeId)) {
          // Update distribution to only include the selected assignee
          return {
            ...item,
            users: [assigneeId],
          }
        }
      }

      // Return unchanged for owners or if no assignee match
      return item
    })

    return {
      result: updatedResult,
      attendance: routingResult.attendance,
    }
  }

  // Return unchanged for OwnerFromSimpleRule (no distributions to apply assignees to)
  return routingResult
}

/**
 * Applies selected assignees to WithDistribution routing result. Matches assignees to distributions
 * by index.
 *
 * @param routingResult - The routing result with distributions
 * @param selectedAssigneeIds - Array of assignee IDs indexed by distribution position
 * @returns Updated routing result with assignees applied, or undefined
 */
export const applySelectedAssigneesToWithDistribution = (
  routingResult?: WithDistribution,
  selectedAssigneeIds?: string[]
): WithDistribution | undefined => {
  if (!routingResult || !selectedAssigneeIds || selectedAssigneeIds.length === 0) {
    return routingResult
  }

  // WithDistribution always has distributions array
  if (routingResult.distributions && routingResult.distributions.length > 0) {
    const updatedDistributions = routingResult.distributions.map((distribution, index) => {
      // Check if we have an assignee for this distribution index
      const assigneeId = selectedAssigneeIds[index]

      if (!assigneeId) {
        // No assignee for this distribution, return unchanged
        return distribution
      }

      // Check if assigneeId is in this distribution's users
      const isAssigneeInDistribution = distribution.users?.includes(assigneeId)

      if (isAssigneeInDistribution) {
        // Update distribution to only include the selected assignee
        return {
          ...distribution,
          users: [assigneeId],
        }
      }
      return distribution
    })

    return {
      distributions: updatedDistributions,
      attendance: routingResult.attendance,
    }
  }

  // Return unchanged if no distributions
  return routingResult
}

export const getDistributionIdsFromOwnershipAssignments = (
  assignments?: OwnershipSchedulingLinkAssignments,
  linkDistributionIds?: string[]
): string[] => {
  if (!assignments) return []

  if (assignments.type === 'WithLegacySingleDistribution') {
    return linkDistributionIds || []
  }

  if (assignments.type === 'WithMixedRouting' && assignments.assignments) {
    const distributionIds: string[] = []
    assignments.assignments.forEach(assignment => {
      if (assignment.type === 'FromDistributionAssignment' && assignment.distributionId) {
        distributionIds.push(assignment.distributionId)
      }
    })
    return distributionIds
  }

  return []
}
